import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MessageCircle, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

/* WhatsApp-Verlauf aufräumen (DSGVO-Prüfung 28.09.2026): Die Brücke hat beim
   Koppeln jeden Einzelchat des Studiohandys übernommen, auch Lieferanten und
   Privates — und dazu automatisch Leads angelegt. Das CRM schlägt vor, der
   Inhaber hakt je Chat an. Endgültig: gelöscht wird wie bei einer DSGVO-Löschung. */
const datum = (iso) => iso ? `${String(iso).slice(8, 10)}.${String(iso).slice(5, 7)}.${String(iso).slice(0, 4)}` : '–';

export default function WhatsAppAufraeumenKarte() {
  const [liste, setListe] = useState(null);
  const [auswahl, setAuswahl] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const lade = async () => {
    try { setListe((await api.get('/wa-bruecke/aufraeumen')).data.vorschlaege || []); setAuswahl(new Set()); }
    catch (e) { setListe(e?.response?.status === 403 ? 'verboten' : []); }
  };
  useEffect(() => { lade(); }, []);
  if (liste === null || liste === 'verboten') return null;

  const umschalten = (id) => setAuswahl(a => { const n = new Set(a); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const loeschen = async () => {
    if (!auswahl.size) return;
    if (!window.confirm(`${auswahl.size} Chat(s) samt Nachrichten endgültig löschen? Automatisch angelegte, nie bearbeitete Leads dazu werden mit entfernt. Das lässt sich nicht rückgängig machen.`)) return;
    setBusy(true);
    try {
      const r = (await api.post('/wa-bruecke/aufraeumen/loeschen', { konversation_ids: [...auswahl] })).data;
      toast.success(`${r.geloescht_chats} Chats und ${r.geloescht_leads} Leads gelöscht`); lade();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen'); }
    setBusy(false);
  };

  return (
    <Card data-testid="wa-aufraeumen">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><MessageCircle className="w-4 h-4" />WhatsApp-Verlauf aufräumen</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">Beim Koppeln hat die Brücke alle Einzelchats des Studiohandys übernommen – auch Lieferanten, Mitarbeiter und Privates. Vorgeschlagen werden geschlossene Chats, die nie aus dem CRM beantwortet wurden und entweder älter als 24 Monate sind, keinem Lead gehören oder nur einen automatisch angelegten, nie bearbeiteten Lead haben. Bitte je Chat prüfen.</p>
        {liste.length === 0 ? <p className="text-xs">Keine Vorschläge – nichts aufzuräumen.</p> : (
          <>
            <div className="flex items-center gap-2 text-xs">
              <button type="button" className="underline" onClick={() => setAuswahl(new Set(liste.map(v => v.konversation_id)))}>alle auswählen</button>
              <button type="button" className="underline" onClick={() => setAuswahl(new Set())}>keine</button>
              <span className="text-muted-foreground">{auswahl.size} von {liste.length} ausgewählt</span>
            </div>
            <ul className="max-h-80 overflow-y-auto divide-y divide-border/50 rounded-md border border-border/60">
              {liste.map(v => (
                <li key={v.konversation_id} className="px-3 py-2 text-xs flex items-start gap-2">
                  <input type="checkbox" className="mt-0.5 accent-marke" checked={auswahl.has(v.konversation_id)} onChange={() => umschalten(v.konversation_id)} aria-label={`${v.name} auswählen`} />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{v.name || `+${v.wa_id}`} <span className="font-normal text-muted-foreground">· +{v.wa_id} · {v.nachrichten} Nachr. · zuletzt {datum(v.letzte_nachricht_am)}</span></div>
                    <div className="text-muted-foreground">{v.gruende.join(' · ')}{v.lead?.wird_geloescht ? ' · Lead wird mit gelöscht' : ''}</div>
                  </div>
                </li>
              ))}
            </ul>
            <Button size="sm" variant="destructive" className="gap-1.5" disabled={busy || !auswahl.size} onClick={loeschen} data-testid="wa-aufraeumen-loeschen">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Ausgewählte endgültig löschen
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
